import { c as _c } from "react/compiler-runtime";
import Button from '@precis-digital/kurama/Button';
import Divider from '@precis-digital/kurama/Divider';
import Grid from '@precis-digital/kurama/Grid';
import Link from '@precis-digital/kurama/Link';
import PlusIcon from '@precis-digital/kurama/icons/PlusIcon';
import PopUp from '@precis-digital/kurama/PopUp';
import Typography from '@precis-digital/kurama/Typography';
import ThemedImage from 'shared/components/Themed/Image';
import emailSymbol from 'public/assets/images/email_symbol.png';
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { type MemberRole } from 'shared/api/clients';
import { type ClientError } from 'shared/api/fetch';
import Dots from 'shared/components/Loader/Dots';
import { makeToastWithLoading } from 'shared/components/Toaster';
import { INTERCOM_ARTICLE_IDS } from 'shared/constants/documentation';
import { useCurrentClient } from 'shared/context/ClientContext';
import { HttpStatusCodes } from 'shared/httpStatus';
import { openIntercomArticle } from 'shared/intercom';
import { useTranslation } from 'shared/translations';
import { useCreateMessage, useQueryMembers, useQueryMessages, useResendMessage, queryKeys as workspaceQueryKeys } from '../../api';
import { findPendingInvite, isDuplicateInviteEmail, isEmailAlreadyMember } from '../../utils';
import { StyledAssignedTextInvite, StyledLeaveWorkspacePopupFooter, StyledUpperText } from '../Popup/styles';
import { MailInput } from './MailInput';
interface InviteMembersPopupProps {
  open: boolean;
  onClose: () => void;
}
interface Invite {
  toEmail: string;
  role: MemberRole;
  clientId: number;
}
type Invites = Invite[];
type InviteOutcome = 'sent' | 'recentlyNotified' | 'failed';

// Reminders are rate limited server-side, so a rejected resend is an expected
// outcome the member owner can act on rather than an application failure.
const isResendRateLimited = (error: unknown): boolean => (error as ClientError).status_code === HttpStatusCodes.TOO_MANY_REQUESTS;
const InviteNewMembersPopup = props => {
  const $ = _c(101);
  const {
    open,
    onClose
  } = props;
  const {
    currentClient
  } = useCurrentClient();
  const {
    t
  } = useTranslation("workspace");
  let t0;
  if ($[0] !== currentClient.id) {
    t0 = {
      clientId: currentClient.id,
      toEmail: "",
      role: "viewer"
    };
    $[0] = currentClient.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const emptyInvite = t0;
  const {
    data: members
  } = useQueryMembers(currentClient.id);
  let t1;
  if ($[2] !== emptyInvite) {
    t1 = [{
      ...emptyInvite
    }];
    $[2] = emptyInvite;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [invites, setInvites] = useState(t1);
  const {
    data: messages
  } = useQueryMessages(currentClient.id);
  let t2;
  if ($[4] !== invites) {
    t2 = (index, role) => {
      const invitesToUpdate = [...invites];
      const invite = invitesToUpdate[index];
      invite.role = role;
      setInvites(invitesToUpdate);
    };
    $[4] = invites;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const setRole = t2;
  let t3;
  if ($[6] !== invites) {
    t3 = (index_0, email) => {
      const invitesToUpdate_0 = [...invites];
      const invite_0 = invitesToUpdate_0[index_0];
      invite_0.toEmail = email;
      setInvites(invitesToUpdate_0);
    };
    $[6] = invites;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const setEmail = t3;
  let t4;
  if ($[8] !== emptyInvite || $[9] !== invites) {
    t4 = () => {
      const updatedInvites = [...invites, {
        ...emptyInvite
      }];
      setInvites(updatedInvites);
    };
    $[8] = emptyInvite;
    $[9] = invites;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const addInvite = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const [validEmails, setValidEmails] = useState(t5);
  let t6;
  if ($[12] !== emptyInvite || $[13] !== invites || $[14] !== validEmails) {
    t6 = (index_1, valid) => {
      const validEmailsToUpdate = [...validEmails];
      validEmailsToUpdate[index_1] = valid;
      invites.map((invite_1, index_2) => {
        if (invite_1.toEmail === emptyInvite.toEmail && invites.length > 1) {
          validEmailsToUpdate[index_2] = true;
        }
        return invite_1;
      });
      setValidEmails(validEmailsToUpdate);
    };
    $[12] = emptyInvite;
    $[13] = invites;
    $[14] = validEmails;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const setValidEmail = t6;
  const {
    mutateAsync: sendInvitation
  } = useCreateMessage();
  const {
    mutateAsync: resendMessage
  } = useResendMessage();
  let t7;
  if ($[16] !== currentClient.id || $[17] !== messages || $[18] !== resendMessage) {
    t7 = async invite_2 => {
      const pendingInvite = findPendingInvite(messages, invite_2.toEmail);
      const messageId = pendingInvite?.id === undefined ? undefined : String(pendingInvite.id);
      if (messageId === undefined) {
        return "failed";
      }
      ;
      try {
        await resendMessage({
          messageId,
          clientId: currentClient.id
        });
        return "sent";
      } catch (t8) {
        const error = t8;
        return isResendRateLimited(error) ? "recentlyNotified" : "failed";
      }
    };
    $[16] = currentClient.id;
    $[17] = messages;
    $[18] = resendMessage;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const resendFailedInvitation = t7;
  let t8;
  if ($[20] !== resendFailedInvitation || $[21] !== sendInvitation) {
    t8 = async invite_3 => {
      try {
        await sendInvitation(invite_3);
        return "sent";
      } catch {
        return await resendFailedInvitation(invite_3);
      }
    };
    $[20] = resendFailedInvitation;
    $[21] = sendInvitation;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const sendOrResendInvitation = t8;
  let t9;
  if ($[23] !== emptyInvite) {
    t9 = invite_4 => invite_4.toEmail !== emptyInvite.toEmail;
    $[23] = emptyInvite;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const shouldHaveEmail = t9;
  const queryClient = useQueryClient();
  const [isSendingInvites, setIsSendingInvites] = useState(false);
  let canSendInvites;
  let handleClose;
  let handleSendInvitations;
  if ($[25] !== currentClient.id || $[26] !== emptyInvite || $[27] !== invites || $[28] !== onClose || $[29] !== queryClient || $[30] !== sendOrResendInvitation || $[31] !== shouldHaveEmail || $[32] !== t || $[33] !== validEmails) {
    handleSendInvitations = async () => {
      const {
        toastOnSuccess,
        toastOnError
      } = makeToastWithLoading();
      if (canSendInvites) {
        setIsSendingInvites(true);
        const results = await Promise.all(invites.filter(shouldHaveEmail).map(async invite_5 => ({
          toEmail: invite_5.toEmail,
          outcome: await sendOrResendInvitation(invite_5)
        })));
        await queryClient.invalidateQueries({
          queryKey: [workspaceQueryKeys.allMessages, currentClient.id]
        });
        setIsSendingInvites(false);
        const emailsWithOutcome = outcome => results.filter(result => result.outcome === outcome).map(_temp);
        const failedEmails = emailsWithOutcome("failed");
        const recentlyNotifiedEmails = emailsWithOutcome("recentlyNotified");
        if (failedEmails.length > 0) {
          toastOnError(t("invite.error", {
            toEmail: failedEmails.join(", ")
          }));
        } else {
          if (recentlyNotifiedEmails.length > 0) {
            toastOnError(t("invite.recentlyNotified", {
              toEmail: recentlyNotifiedEmails.join(", ")
            }));
          } else {
            toastOnSuccess(`Member invitation${results.length > 1 ? "s" : ""} successfully sent`);
          }
        }
        handleClose();
      }
    };
    canSendInvites = validEmails.length > 0 && !validEmails.includes(false);
    handleClose = () => {
      setInvites([{
        ...emptyInvite
      }]);
      setValidEmails([]);
      onClose();
    };
    $[25] = currentClient.id;
    $[26] = emptyInvite;
    $[27] = invites;
    $[28] = onClose;
    $[29] = queryClient;
    $[30] = sendOrResendInvitation;
    $[31] = shouldHaveEmail;
    $[32] = t;
    $[33] = validEmails;
    $[34] = canSendInvites;
    $[35] = handleClose;
    $[36] = handleSendInvitations;
  } else {
    canSendInvites = $[34];
    handleClose = $[35];
    handleSendInvitations = $[36];
  }
  let t10;
  if ($[37] !== emptyInvite || $[38] !== invites) {
    t10 = email_0 => {
      if (email_0 === emptyInvite.toEmail) {
        return false;
      }
      return isDuplicateInviteEmail(email_0, invites.map(_temp2));
    };
    $[37] = emptyInvite;
    $[38] = invites;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  const isDuplicate = t10;
  let t11;
  if ($[40] !== members) {
    t11 = email_1 => {
      if (members === undefined) {
        return false;
      }
      return isEmailAlreadyMember(email_1, members.map(_temp3));
    };
    $[40] = members;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  const isAlreadyMember = t11;
  let t12;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ThemedImage src={emailSymbol.src} width={544} height={179} alt="email symbol" style={{
      maxWidth: "100%",
      height: "auto"
    }} />;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t) {
    t13 = t("titles.inviteMembersPopup");
    $[43] = t;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== currentClient.name || $[46] !== t13) {
    t14 = <StyledUpperText><Typography variant="h2">{" "}{t13} {currentClient.name}{" "}</Typography></StyledUpperText>;
    $[45] = currentClient.name;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== t) {
    t15 = t("titles.emailAddress");
    $[48] = t;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== t15) {
    t16 = <Typography variant="h3"> {t15} </Typography>;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== t) {
    t17 = t("titles.accountPermissions");
    $[52] = t;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = openIntercomArticle(INTERCOM_ARTICLE_IDS.USER_ROLES_AND_PERMISSIONS);
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t) {
    t19 = t("titles.accountPermissionsLink");
    $[55] = t;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t19) {
    t20 = <Link variant="h4" onClick={t18}>{t19}</Link>;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t17 || $[60] !== t20) {
    t21 = <StyledAssignedTextInvite variant="h4">{t17}{" "}{t20}.</StyledAssignedTextInvite>;
    $[59] = t17;
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== handleSendInvitations || $[63] !== invites || $[64] !== isAlreadyMember || $[65] !== isDuplicate || $[66] !== setEmail || $[67] !== setRole || $[68] !== setValidEmail) {
    let t23;
    if ($[70] !== handleSendInvitations || $[71] !== isAlreadyMember || $[72] !== isDuplicate || $[73] !== setEmail || $[74] !== setRole || $[75] !== setValidEmail) {
      t23 = (invite_7, index_3) => <MailInput key={index_3} index={index_3} setEmail={setEmail} setValidEmail={setValidEmail} setRole={setRole} email={invite_7.toEmail} role={invite_7.role} duplicateEmailCheck={isDuplicate} alreadyMemberEmailCheck={isAlreadyMember} handleSendInvitations={handleSendInvitations} />;
      $[70] = handleSendInvitations;
      $[71] = isAlreadyMember;
      $[72] = isDuplicate;
      $[73] = setEmail;
      $[74] = setRole;
      $[75] = setValidEmail;
      $[76] = t23;
    } else {
      t23 = $[76];
    }
    t22 = invites.map(t23);
    $[62] = handleSendInvitations;
    $[63] = invites;
    $[64] = isAlreadyMember;
    $[65] = isDuplicate;
    $[66] = setEmail;
    $[67] = setRole;
    $[68] = setValidEmail;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  let t23;
  if ($[77] !== t22) {
    t23 = <Grid display="flex" flexDirection="column" gap="16px" paddingBottom="24px">{t22}</Grid>;
    $[77] = t22;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <PlusIcon />;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  let t25;
  if ($[80] !== t) {
    t25 = t("buttons.addMoreMembers");
    $[80] = t;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  let t26;
  if ($[82] !== addInvite || $[83] !== t25) {
    t26 = <Button leftIcon={t24} onClick={addInvite} scheme="light" variant="outlined">{t25}</Button>;
    $[82] = addInvite;
    $[83] = t25;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Divider />;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  let t28;
  if ($[86] !== canSendInvites || $[87] !== handleClose || $[88] !== handleSendInvitations || $[89] !== isSendingInvites || $[90] !== t) {
    t28 = <StyledLeaveWorkspacePopupFooter>{isSendingInvites ? <Dots /> : <><Button onClick={handleClose} scheme="light" variant="outlined">{t("buttons.cancel")}</Button><Button onClick={() => {
          handleSendInvitations();
        }} scheme="light" variant="filled" disabled={!canSendInvites}>{t("buttons.sendInvitations")}</Button></>}</StyledLeaveWorkspacePopupFooter>;
    $[86] = canSendInvites;
    $[87] = handleClose;
    $[88] = handleSendInvitations;
    $[89] = isSendingInvites;
    $[90] = t;
    $[91] = t28;
  } else {
    t28 = $[91];
  }
  let t29;
  if ($[92] !== handleClose || $[93] !== open || $[94] !== t14 || $[95] !== t16 || $[96] !== t21 || $[97] !== t23 || $[98] !== t26 || $[99] !== t28) {
    t29 = <PopUp handleOpen={handleClose} open={open}>{t12}{t14}{t16}{t21}{t23}{t26}{t27}{t28}</PopUp>;
    $[92] = handleClose;
    $[93] = open;
    $[94] = t14;
    $[95] = t16;
    $[96] = t21;
    $[97] = t23;
    $[98] = t26;
    $[99] = t28;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  return t29;
};
export default InviteNewMembersPopup;
function _temp(result_0) {
  return result_0.toEmail;
}
function _temp2(invite_6) {
  return invite_6.toEmail;
}
function _temp3(member) {
  return member.email;
}